import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import EntityForm from "../forms/entity-form/EntityForm";
import ErrorBoundary from "./ErrorBoundary";
import { useEntityFormSelector, useEntityFormActions } from "../../store/entityForm";
import { useQueryCacheInvalidator } from "../../hooks/useQueryCacheInvalidator";
import { getEntityQueryKey } from "../../api/entity";

/**
 * Global EntityForm Manager
 *
 * It listens to the entityForm Zustand store and renders the EntityForm modal.
 *
 * Usage:
 * - <EntityFormManager /> is placed on App.tsx (main layout)
 * - Use useEntityFormActions() hook anywhere to open forms
 *
 */
export default function EntityFormManager() {
  const queryClient = useQueryClient();
  const { open, mode, endpoint, existingItem, callbacks } = useEntityFormSelector();
  const { closeForm } = useEntityFormActions();
  const { invalidateDataDrawerCache, updateItemInCache } = useQueryCacheInvalidator();

  function clearCache(scope?: string) {
    invalidateDataDrawerCache(scope);
    queryClient.invalidateQueries({ queryKey: ["endpoint-entity-editor"] });
    queryClient.invalidateQueries({ queryKey: ["bulk-entity-fetch"] });
  }

  const handleItemCreated = useCallback(
    async (newItem: any) => {
      console.log("[EntityFormManager] Item created:", newItem);

      // Drawer cache for this endpoint was already invalidated (scoped) by EntityForm
      // right after the create call. Only invalidate the other caches here.
      if (newItem) {
        queryClient.invalidateQueries({ queryKey: ["endpoint-entity-editor"] });
        queryClient.invalidateQueries({ queryKey: ["bulk-entity-fetch"] });
      }

      // CUSTOM CALLBACK (if provided)
      if (callbacks?.onUpdated) {
        await callbacks.onUpdated(newItem);
      }

      closeForm();
    },
    [endpoint, queryClient, closeForm, callbacks]
  );

  const handleItemUpdated = useCallback(
    async (updatedItem: any) => {
      if (updatedItem) {
        updateItemInCache(updatedItem);

        queryClient.setQueryData(getEntityQueryKey(updatedItem.id, endpoint, "L2"), (oldData: any) => ({
          ...(oldData || {}),
          ...updatedItem
        }));

        // Only invalidate non-drawer caches — drawer is handled above + by EntityForm.
        queryClient.invalidateQueries({ queryKey: ["endpoint-entity-editor"] });
        queryClient.invalidateQueries({ queryKey: ["bulk-entity-fetch"] });
      }

      // CUSTOM CALLBACK (if available)
      if (callbacks?.onUpdated) {
        await callbacks.onUpdated(updatedItem);
      }

      closeForm();
    },
    [endpoint, queryClient, closeForm, callbacks, updateItemInCache]
  );

  const handleItemDeleted = useCallback(
    async (deletedItemId: number | string) => {
      console.log("[EntityFormManager] Item deleted:", deletedItemId);

      // cache invalidation
      if (endpoint) {
        clearCache(endpoint.tableName);
      }

      // CUSTOM CALLBACK
      if (callbacks?.onDeleted) {
        await callbacks.onDeleted(deletedItemId);
      }

      closeForm();
    },
    [endpoint, queryClient, closeForm, callbacks]
  );

  function handleCancel(item?: any) {
    closeForm?.();
    if (callbacks) {
      callbacks?.onCancel?.(item);
    }
  }

  return (
    <ErrorBoundary>
      <EntityForm
        open={open}
        onClose={closeForm}
        endpoint={endpoint}
        mode={mode}
        existingItem={existingItem}
        onItemCreated={handleItemCreated}
        onItemUpdated={handleItemUpdated}
        onItemDeleted={handleItemDeleted}
        onCancel={handleCancel}
      />
    </ErrorBoundary>
  );
}
